import Lock from '@/assets/svg/lock.svg?react';
import LockIconRed from '@/assets/svg/lock-red.svg?react';
import CheckIcon from '@/assets/svg/check-mark-validation.svg?react';
import AuthWrapper from '@/layouts/AuthWrapper';
import { useForm, Controller } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { ISetPasswordRequest } from '@/services/auth/types';
import { Box, Text, Stack, HStack, Icon, Flex } from '@chakra-ui/react';
import CustomInput from '@/components/common/Input';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetPassword } from '@/services/auth/auth';
import { AxiosError } from 'axios';
import { NAVIGATION_ROUTES } from '@/routes/constants';
import CustomButton from '@/components/common/Button';
import useCustomToast from '@/components/common/Toast';

interface ISetPasswordRequestUI {
  password: string;
  confirmPassword: string;
}

const passwordSchema = yup.object().shape({
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Must be at least 8 characters')
    .matches(/^(?=.*[!@#$%^&*])/, 'Must contain one special character'),
  confirmPassword: yup
    .string()
    .required('Confirm password is required')
    .oneOf([yup.ref('password')], 'Passwords do not match'),
});

const SetPassword = () => {
  const { showToast } = useCustomToast();
  const navigate = useNavigate();
  const { mutateAsync: setPassword, isLoading } = useSetPassword();
  const { state } = useLocation();
  const [email, setEmail] = useState(state?.email);

  useEffect(() => {
    if (state) {
      setEmail(state.email);
    }
  }, [state]);

  const {
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(passwordSchema),
  });

  const password = watch('password');
  const isMinLengthValid = password?.length >= 8;
  const hasSpecialChar = /[\W_]/.test(password);

  const onSubmit = (data: ISetPasswordRequestUI) => {
    const payload: ISetPasswordRequest = {
      email,
      password: data.password,
      confirm_password: data.confirmPassword,
    };
    setPassword(payload)
      .then(() => {
        navigate(NAVIGATION_ROUTES.REGISTRATION.SUCCESS);
      })
      .catch((error) => {
        if (error instanceof AxiosError) {
          // setError("password", {
          //   type: "manual",
          //   message: error.response?.data.error,
          // });
          showToast('error', error.response?.data.error);
        }
      });
  };

  return (
    <Box id="set-password" fontFamily={'primary'}>
      <AuthWrapper>
        <Flex direction={'column'} gap={'32px'} maxW="400px" mx="auto" p="4">
          <Icon
            as={LockIconRed}
            boxSize={12}
            p={3}
            borderRadius={'md'}
            color={'brand.primary.DEFAULT'}
            border={'1px solid'}
            borderColor={'border.default'}
          />
          <Flex direction="column" gap="1">
            <Text fontSize="4xl" fontWeight="semibold">
              Set password
            </Text>
            <Text color="text.secondary" fontSize="sm">
              Your new password must be different from previously used passwords.
            </Text>
          </Flex>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex direction="column" gap="5">
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <CustomInput
                    label="Password"
                    type="password"
                    placeholder="Enter password"
                    error={errors.password}
                    leftIcon={<Lock />}
                    {...field}
                  />
                )}
              />
              <Flex direction={'column'} gap={'6'}>
                <Flex direction="column" gap="1">
                  <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        label="Confirm Password"
                        type="password"
                        placeholder="Enter password"
                        error={errors.confirmPassword}
                        leftIcon={<Lock />}
                        {...field}
                      />
                    )}
                  />
                  {/* Password Guidelines */}
                  <Stack spacing={1}>
                    <HStack>
                      <Box
                        bg={isMinLengthValid ? 'status.success' : 'border.default'}
                        borderRadius="full"
                        p={2}
                        height={'14px'}
                        width={'14px'}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Icon as={CheckIcon} color="red" fontSize={'8px'} />
                      </Box>
                      <Text fontSize="xs" color={'text.secondary'} fontFamily={'primary'}>
                        Must be at least 8 characters
                      </Text>
                    </HStack>

                    <HStack>
                      <Box
                        bg={hasSpecialChar ? 'status.success' : 'border.default'}
                        borderRadius="full"
                        p={2}
                        height={'14px'}
                        width={'14px'}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Icon as={CheckIcon} color="white" fontSize={'8px'} />
                      </Box>
                      <Text fontSize="xs" color={'text.secondary'} fontFamily={'primary'}>
                        Must contain one special character
                      </Text>
                    </HStack>
                  </Stack>
                </Flex>
                <CustomButton
                  variant={'primary'}
                  isLoading={isSubmitting || isLoading}
                  type="submit"
                  colorScheme="red"
                  width="full"
                >
                  Create Password
                </CustomButton>
              </Flex>
            </Flex>
          </form>
        </Flex>
      </AuthWrapper>
    </Box>
  );
};

export default SetPassword;
